import { useMemo, useState, useEffect } from 'react'
import { useGetInspectorDataQuery } from '@sketch/gql-types'
import { InspectorDataArtboard } from '../inspectorDataType'
import { ErrorHandler } from '@sketch/tracing'

const DEFAULT_TIMEOUT = 5 * 60 * 1000 // 5 minutes
const POLL_INTERVAL = 4 * 1000 // 4 seconds
type PollingStatus = 'IDLE' | 'POLLING_TIMEOUT' | 'POLLING' | 'DONE'

/**
 * Fetch inspector data and retry until the data is ready if that's not the case yet.
 * To skip the request (like when inspector is not opened, just pass undefined as artboardId)
 * Note: This request will trigger on the server side the generation of the inspector data on demand.
 */
export function useGetInspectorData(artboardId: string | undefined) {
  const [hasParsingError, setHasParsingError] = useState(false)

  const {
    loading,
    data,
    startPolling,
    stopPolling,
    error: apolloError,
  } = useGetInspectorDataQuery({
    variables: { artboardId },
    skip: !artboardId,
  })

  const inspectorDataStatus = data?.inspectorData?.inspector?.status

  const pollingStatus = usePollDataIfNeeded(
    inspectorDataStatus,
    startPolling,
    stopPolling,
    Boolean(apolloError) || hasParsingError
  )

  // The inspector data is returned as a json string that we need to parse
  const inspectorStringifiedJsonData = data?.inspectorData?.inspector?.data

  return useMemo(() => {
    let inspectorData: InspectorDataArtboard | null = null

    if (inspectorStringifiedJsonData) {
      try {
        inspectorData = JSON.parse(inspectorStringifiedJsonData)
      } catch (exception) {
        setHasParsingError(true)
        ErrorHandler.shouldNeverHappen(
          `Error parsing inspector data json string for artboard ${artboardId}. Exception: ${exception}`
        )
      }
    }

    return {
      isFetchingInspectorData: loading,
      inspectorDataIsReady: inspectorDataStatus === 'READY',
      inspectorApolloError: apolloError,
      inspectorData: inspectorData,
      hasDataParsingError: hasParsingError,
      pollingStatus,
    }
  }, [
    inspectorStringifiedJsonData,
    loading,
    inspectorDataStatus,
    apolloError,
    hasParsingError,
    pollingStatus,
    artboardId,
  ])
}

/**
 * Start/stop the polling mechanism based on the current status of the inspector data.
 * We generate the data on demand, poll every 4 seconds to see if it's ready
 * if the status is pending.
 * Note: This is a workaround to avoid having to create a new subscription.
 */

function usePollDataIfNeeded(
  inspectorDataStatus: 'PENDING' | 'READY' | undefined | null,
  startPolling: (pollInterval: number) => void,
  stopPolling: () => void,
  hasError: boolean
) {
  const [pollingStatus, setPollingStatus] = useState<PollingStatus>('IDLE')

  useEffect(() => {
    if (
      pollingStatus === 'IDLE' &&
      inspectorDataStatus === 'PENDING' &&
      !hasError
    ) {
      // Start polling
      startPolling(POLL_INTERVAL)
      setPollingStatus('POLLING')
    } else if (
      pollingStatus === 'POLLING' &&
      (inspectorDataStatus !== 'PENDING' || hasError)
    ) {
      // stop polling
      stopPolling()
      setPollingStatus('DONE')
    }
  }, [hasError, inspectorDataStatus, pollingStatus, startPolling, stopPolling])

  // Stop polling after DEFAULT_TIMEOUT to avoid polling indefinitely
  // if something went wrong.
  useEffect(() => {
    if (pollingStatus !== 'POLLING') {
      return
    }

    const timeoutId = window.setTimeout(() => {
      stopPolling()
      setPollingStatus('POLLING_TIMEOUT')
    }, DEFAULT_TIMEOUT)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [pollingStatus, stopPolling])

  return pollingStatus
}
